import useIdentity, { Identity } from "@/features/ui/hooks/use-identity";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useGetMySelf from "@/features/auth/use-get-my-self";
import useMembership from "@/shared/hooks/useMembership";
import { Flex, Skeleton } from "@chakra-ui/react";
import AppNavigationLink from "@/features/ui/app-navigation/app-navigation-link";
import { useLocation } from "react-router-dom";
import type { Location } from "@remix-run/router";
import { AppUtils } from "@/features/ui/utils/app-utils";
import { TransportationStatuses } from "@/features/transportations/transportation.type";
import { PathPickMembershipStatus } from "@/features/types";
import { AssetTypes } from "../../../public/icons-v1_1";
import PathPickBasketNavCountBadge from "@/features/pathpick/components/nav/PathPickBasketNavCountBadge";
import InquiryNavCountBadge from "@/features/inquiries/components/InquiryNavCountBadge";
import useRestrictedFeatures from "@/features/auth/use-restricted-features";
import {
  PATHPICK_MOBILE_CHILD_KEYS,
  PATHPICK_MOBILE_SECTION_KEY,
} from "@/features/pathpick/constants/mobile-navigation";

export interface LiteralItem {
  pathname: string;
  query?: { [key: string]: string | string[] | null | boolean };
  icon: AssetTypes;
  children?: LiteralItem[];
  predicates?: ((literal: LiteralItem, location: Location) => boolean)[];
  key: string;
  // 메뉴 라벨 우측 끝에 붙는 부가 요소 (예: 담은 상품 개수 배지)
  rightElement?: React.ReactNode;
}

export interface Item extends Omit<LiteralItem, "children"> {
  children: Item[];
  isCollapsed: boolean;
  isActive: boolean;
}

function getLiterals(
  identity: Identity | null,
  features: string[],
  pathPickMembershipStatus: PathPickMembershipStatus
): LiteralItem[] {
  const transportations: LiteralItem = {
    key: "common:navigation.transportations",
    pathname: "/transportations",
    icon: "airplane",
    children: [
      {
        key: "common:navigation.all_transportations",
        pathname: "/transportations",
        icon: "dot",
        query: {
          status__in: null,
        },
        predicates: [AppUtils.isContainsLiteral],
      },
      {
        key: "common:navigation.overseas_transportations",
        pathname: "/transportations",
        icon: "dot",
        query: {
          status__in: AppUtils.subList(
            TransportationStatuses,
            "WAYBILL_ISSUED",
            "FLIGHT_ARRIVED"
          ),
        },
        predicates: [AppUtils.isContainsLiteral],
      },
      {
        key: "common:navigation.customs",
        pathname: "/transportations",
        icon: "dot",
        query: {
          status__in: AppUtils.subList(
            TransportationStatuses,
            "CUSTOMS_DECLARED",
            "CUSTOMS_EXPORT_DECLARED"
          ),
        },
        predicates: [AppUtils.isContainsLiteral],
      },
      {
        key: "common:navigation.domestic_transportations",
        pathname: "/transportations",
        icon: "dot",
        query: {
          status__in: AppUtils.subList(
            TransportationStatuses,
            "DOMESTIC_DISPATCHING",
            "DOMESTIC_SHIPPING_COMPLETED"
          ),
        },
        predicates: [AppUtils.isContainsLiteral],
      },
    ],
  };

  const invoices: LiteralItem = {
    key: "common:navigation.invoices",
    pathname: "/invoices",
    icon: "receipt",
    children: [
      {
        key: "order_confirms",
        pathname: "/order-confirmation-invoices",
        icon: "dot",
        predicates: [
          AppUtils.isContainsLiteral,
          AppUtils.isLiteralEditOrUpload,
        ],
      },
      {
        key: "proformas",
        pathname: "/proforma-invoices",
        icon: "dot",
        predicates: [
          AppUtils.isContainsLiteral,
          AppUtils.isLiteralEditOrUpload,
        ],
      },
      {
        key: "payments",
        pathname: "/payments",
        icon: "dot",
        predicates: [
          AppUtils.isContainsLiteral,
          AppUtils.isLiteralEditOrUpload,
        ],
      },
      {
        key: "bank_receipts",
        pathname: "/bank-remittance-receipts",
        icon: "dot",
        predicates: [
          AppUtils.isContainsLiteral,
          AppUtils.isLiteralEditOrUpload,
        ],
      },
      {
        key: "deposit_invoices",
        pathname: "/deposit-invoices",
        icon: "dot",
        predicates: [
          AppUtils.isContainsLiteral,
          AppUtils.isLiteralEditOrUpload,
        ],
      },
      {
        key: "credits",
        pathname: "/credit-invoices",
        icon: "dot",
        predicates: [
          AppUtils.isContainsLiteral,
          AppUtils.isLiteralEditOrUpload,
        ],
      },
    ],
  };

  const inventories: LiteralItem = {
    key: "inventories",
    pathname: "/inventories",
    icon: "package",
  };

  if (identity?.company?.type === "BUYER") {
    // 멤버십 상태에 따라 패스픽 하위 메뉴가 바뀐다(시안):
    // 결제 전(NONE)에는 멤버십만, 그 외(체험/구독/만료)에는 기능 메뉴 전체.
    // TODO(시안): 패스픽 안내 페이지가 만들어지면 NONE 메뉴에 "패스픽 안내"를 추가한다.
    const pathPickIntroChildren: LiteralItem[] = [
      {
        key: "common:navigation.path_pick_membership",
        pathname: "/pathpick/membership",
        icon: "dot",
        predicates: [AppUtils.isContainsLiteral],
      },
    ];

    const pathPickMenu: LiteralItem = {
      key: "common:navigation.path_pick",
      pathname: "/pathpick",
      icon: "star",
      children: pathPickMembershipStatus === "NONE" ? pathPickIntroChildren : [
        {
          key: "common:navigation.path_pick_search_products",
          pathname: "/pathpick/search-products",
          icon: "dot",
          predicates: [AppUtils.isContainsLiteral],
        },
        {
          key: "common:navigation.path_pick_basket",
          pathname: "/pathpick/basket",
          icon: "dot",
          predicates: [AppUtils.isContainsLiteral],
          rightElement: <PathPickBasketNavCountBadge />,
        },
        {
          key: "common:navigation.path_pick_sync_download_history",
          pathname: "/pathpick/sync-download-history",
          icon: "dot",
          predicates: [AppUtils.isContainsLiteral],
        },
        {
          key: "common:navigation.path_pick_order_history",
          pathname: "/pathpick/order-history",
          icon: "dot",
          // 리스트(정확 일치)와 상세(/order-history/:id) 모두 하이라이트되도록 prefix 매칭
          predicates: [
            (_literal, location) => {
              const path = location.pathname.replace(/^\/(ko|it|en)/, "");
              return (
                path === "/pathpick/order-history" ||
                path.startsWith("/pathpick/order-history/")
              );
            },
          ],
        },
        {
          key: "common:navigation.path_pick_membership",
          pathname: "/pathpick/membership",
          icon: "dot",
          predicates: [AppUtils.isContainsLiteral],
        },
      ],
    };

    const buyerLiterals: LiteralItem[] = [
      {
        key: "market_dashboard",
        pathname: "/dashboard",
        icon: "graph-up",
      },
      {
        key: "my_dashboard",
        pathname: "/my-dashboard",
        icon: "chart-bar",
      },
    ];

    // 상품 시세 및 마진 분석 — 모든 바이어에게 연다(2026-11-04, 전엔 SKU_DASHBOARD 기능 플래그로 우노 테스트만)
    buyerLiterals.push({
      key: "sku_dashboard",
      pathname: "/sku-dashboard",
      icon: "document-text-search",
    });

    return [
      ...buyerLiterals,
      {
        key: "navigation.buyer.order",
        pathname: "/line-sheets",
        icon: "document-text-search",
        children: [
          {
            key: "common:navigation.stock",
            pathname: "/line-sheets",
            icon: "dot",
            query: {
              status__in: null,
              type__eq: "STOCK",
            },
            predicates: [
              AppUtils.isContainsLiteral,
              (_lit, location) => {
                const path = location.pathname.replace(/^\/(ko|it|en)/, "");
                const searchParams = new URLSearchParams(location.search);

                return (
                  path.startsWith("/order-sheets") &&
                  searchParams.has("line_sheet_set_id") &&
                  searchParams.get("nav.type__eq") === "STOCK"
                );
              },
            ],
          },
          {
            key: "common:navigation.pre_order",
            pathname: "/line-sheets",
            icon: "dot",
            query: {
              status__in: null,
              type__eq: "SEASON",
            },
            predicates: [
              AppUtils.isContainsLiteral,
              (_lit, location) => {
                const path = location.pathname.replace(/^\/(ko|it|en)/, "");
                const searchParams = new URLSearchParams(location.search);

                return (
                  path.startsWith("/order-sheets") &&
                  searchParams.has("line_sheet_set_id") &&
                  searchParams.get("nav.type__eq") === "SEASON"
                );
              },
            ],
          },
          // 단품오더 메뉴는 뺐다 — 라우트가 없다(pages/index.tsx).
        ],
      },
      {
        key: "navigation.buyer.order_management",
        pathname: "/order-sheets",
        icon: "document-text-edit",
        children: [
          {
            key: "common:navigation.buyer.stock_order_history",
            pathname: "/order-sheets",
            icon: "dot",
            query: {
              "lineSheetSet.type__eq": "STOCK",
              statusGroup__eq: "ALL",
            },
            predicates: [
              AppUtils.isContainsLiteral,
              (_lit, location) => {
                const path = location.pathname.replace(/^\/(ko|it|en)/, "");
                const searchParams = new URLSearchParams(location.search);
                return (
                  path.startsWith("/order-sheet-sets/order-history") &&
                  searchParams.get("line_sheet_set.type__eq") === "STOCK"
                );
              },
              (_lit, location) => {
                const path = location.pathname.replace(/^\/(ko|it|en)/, "");
                const searchParams = new URLSearchParams(location.search);
                // /order-sheets 경로이면서 status_group__eq 쿼리가 존재하면 활성 상태로 인정
                return (
                  path === "/order-sheets" &&
                  searchParams.has("status_group__eq") &&
                  searchParams.get("line_sheet_set.type__eq") === "STOCK"
                );
              },
            ],
          },
          {
            key: "common:navigation.buyer.season_order_history",
            pathname: "/order-sheets",
            icon: "dot",
            query: {
              "lineSheetSet.type__eq": "SEASON",
              statusGroup__eq: "ALL",
            },
            predicates: [
              AppUtils.isContainsLiteral,
              (_lit, location) => {
                const path = location.pathname.replace(/^\/(ko|it|en)/, "");
                const searchParams = new URLSearchParams(location.search);
                return (
                  path.startsWith("/order-sheet-sets/order-history") &&
                  searchParams.get("line_sheet_set.type__eq") === "SEASON"
                );
              },
              (_lit, location) => {
                const path = location.pathname.replace(/^\/(ko|it|en)/, "");
                const searchParams = new URLSearchParams(location.search);
                // /order-sheets 경로이면서 status_group__eq 쿼리가 존재하면 활성 상태로 인정
                return (
                  path === "/order-sheets" &&
                  searchParams.has("status_group__eq") &&
                  searchParams.get("line_sheet_set.type__eq") === "SEASON"
                );
              },
            ],
          },
          // {
          //   key: "common:navigation.buyer.billing_management",
          //   pathname: "/settlement-management",
          //   icon: "dot",
          // },
          // {
          //   key: "common:navigation.pre_order",
          //   pathname: "/order-sheets",
          //   icon: "dot",
          //   query: {
          //     "lineSheetSet.type__eq": "SEASON",
          //     status__in: [
          //       "WORKING_DRAFT",
          //       "ORDERED_TO_AGENCY",
          //       "AGENCY_CONFIRMED",
          //       "ORDERED_TO_BOUTIQUE",
          //       "RESUBMIT_REQUIRED",
          //       "RESUBMIT_COMPLETED",
          //       // "MODIFICATION_REQUIRED",
          //       // "MODIFICATION_COMPLETED",
          //       "BOUTIQUE_CONFIRMED",
          //       "COMPLETED",
          //     ],
          //   },
          // },
          // {
          //   key: "navigation.pre_order_confirmation",
          //   pathname: "/order-confirmation-invoices",
          //   icon: "dot",
          //   predicates: [
          //     AppUtils.isContainsLiteral,
          //     AppUtils.isLiteralEditOrUpload,
          //   ],
          // },
          // {
          //   key: "common:navigation.stock",
          //   pathname: "/order-sheets",
          //   icon: "dot",
          //   query: {
          //     "lineSheetSet.type__eq": "STOCK",
          //     status__in: [
          //       "WORKING_DRAFT",
          //       "ORDERED_TO_AGENCY",
          //       "AGENCY_CONFIRMED",
          //       "ORDERED_TO_BOUTIQUE",
          //       "RESUBMIT_REQUIRED",
          //       "RESUBMIT_COMPLETED",
          //       // "MODIFICATION_REQUIRED",
          //       // "MODIFICATION_COMPLETED",
          //       "BOUTIQUE_CONFIRMED",
          //       "COMPLETED",
          //     ],
          //   },
          // },
          // 단품오더 내역 메뉴도 뺐다(라우트 없음). 모바일 규칙(MOBILE_MENU_RULES)이
          // 이 항목만 남기게 돼 있어, 모바일에서는 '오더 관리' 메뉴 자체가 빠진다.
          {
            key: "navigation.settlement_management",
            pathname: "/order-sheets/settlement",
            icon: "dot",
            query: {
              type: "FEE",
            },
            predicates: [
              (literal, location) => {
                const path = location.pathname.replace(/^\/(ko|it|en)/, "");
                return path === literal.pathname;
              },
            ],
          },
        ],
      },
      // 패스픽 메뉴는 기존 바이어 메뉴를 가리지 않고 맨 아래에 덧붙인다.
      // 상태와 무관하게 항상 노출 — 하위 메뉴 구성만 상태로 갈린다(시안).
      pathPickMenu,
      // {
      //   key: "common:payment",
      //   pathname: "/invoices",
      //   icon: "bank",
      //   children: [
      //     {
      //       key: "navigation.manage_payment",
      //       pathname: "/payments",
      //       icon: "dot",
      //       predicates: [
      //         AppUtils.isContainsLiteral,
      //         AppUtils.isLiteralEditOrUpload,
      //       ],
      //     },
      //     {
      //       key: "bank_receipt",
      //       pathname: "/bank-remittance-receipts",
      //       icon: "dot",
      //       predicates: [
      //         AppUtils.isContainsLiteral,
      //         AppUtils.isLiteralEditOrUpload,
      //       ],
      //     },
      //   ],
      // },
      // {
      //   key: "navigation.buyer.my_stock",
      //   pathname: "/inventories",
      //   icon: "package",
      // },
      // transportations,
    ];
  } else if (identity?.company?.type === "AGENCY") {
    if (identity?.company?.id == 1) {
      return [
        {
          key: "navigation.line_sheets",
          pathname: "/line-sheets",
          icon: "copy",
          children: [
            {
              key: "common:navigation.all_line_sheets",
              pathname: "/line-sheets",
              icon: "dot",
              query: {
                is_published__eq: null,
                is_deleted__eq: null,
              },
            },
            {
              key: "common:navigation.not_published_line_sheets",
              pathname: "/line-sheets",
              icon: "dot",
              query: {
                is_published__eq: false,
                is_deleted__eq: false,
              },
            },
            {
              key: "common:navigation.published_line_sheets",
              pathname: "/line-sheets",
              icon: "dot",
              query: {
                is_published__eq: true,
                is_deleted__eq: false,
              },
            },
            {
              key: "common:navigation.deleted_line_sheets",
              pathname: "/line-sheets",
              icon: "dot",
              query: {
                is_deleted__eq: true,
              },
            },
          ],
        },
        {
          key: "navigation.ordered_line_sheets",
          pathname: "/order-sheets",
          icon: "clipboard-check",
          children: [
            {
              key: "navigation.all_ordered_line_sheets",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: [
                  "ORDERED_TO_AGENCY",
                  "AGENCY_CONFIRMED",
                  "ORDERED_TO_BOUTIQUE",
                  "RESUBMIT_REQUIRED",
                  "RESUBMIT_COMPLETED",
                  "CANCELED",
                  // "MODIFICATION_REQUIRED",
                  // "MODIFICATION_COMPLETED",
                  "BOUTIQUE_CONFIRMED",
                  "BUYER_CONFIRMED",
                  "TRANSPORTATION_ING",
                  "TRANSPORTATION_COMPLETED",
                  "COMPLETED",
                ],
              },
            },
            {
              key: "common:navigation.order_submitted",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["ORDERED_TO_AGENCY"],
              },
            },
            // {
            //   key: "common:navigation.agency_confirmed_line_sheets",
            //   pathname: "/order-sheets",
            //   icon: "dot",
            //   query: {
            //     order_sheet_status__in: ["AGENCY_CONFIRMED"],
            //   },
            // },
            {
              key: "common:navigation.order_consulting",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["ORDERED_TO_BOUTIQUE"],
              },
            },
            {
              key: "common:navigation.buyer_modification_required",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["RESUBMIT_REQUIRED"],
              },
            },
            {
              key: "common:navigation.resubmit_completed",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["RESUBMIT_COMPLETED"],
              },
            },
            {
              key: "common:navigation.buyer_confirm_required",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["BOUTIQUE_CONFIRMED"],
              },
            },
            {
              key: "common:navigation.order_confirmed",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["BUYER_CONFIRMED"],
              },
            },
            {
              key: "common:navigation.transportation_ing",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["TRANSPORTATION_ING"],
              },
            },
            {
              key: "common:navigation.transportation_completed",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["TRANSPORTATION_COMPLETED"],
              },
            },
            {
              key: "common:navigation.completed_line_sheets",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["COMPLETED"],
              },
            },
            {
              key: "common:navigation.canceled_line_sheets",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["CANCELED"],
              },
            },
          ],
        },
        // 디파짓/크레딧 거래 내역 메뉴는 뺐다 — 크레딧 목록으로 충분하다.
        // 문의/답변 — 오더 상태와 상관없이(제출 전 오더 포함) 문의가 있는 오더를 모아 본다
        {
          key: "common:navigation.inquiries",
          pathname: "/inquiries",
          icon: "cs",
          rightElement: <InquiryNavCountBadge />,
        },
        {
          key: "credits",
          pathname: "/credit-invoices",
          icon: "coin",
          predicates: [
            AppUtils.isContainsLiteral,
            AppUtils.isLiteralEditOrUpload,
          ],
        },
        {
          key: "deposit_invoices",
          pathname: "/deposit-invoices",
          icon: "bank",
          predicates: [
            AppUtils.isContainsLiteral,
            AppUtils.isLiteralEditOrUpload,
          ],
        },
        {
          key: "common:navigation.gmail",
          pathname: "/gmails",
          icon: "mail",
        },
        // 단품오더 메뉴는 뺐다 — 라우트가 없다(pages/index.tsx).
      ];
    } else {
      return [
        {
          key: "navigation.line_sheets",
          pathname: "/line-sheets",
          icon: "copy",
          children: [
            {
              key: "common:navigation.all_line_sheets",
              pathname: "/line-sheets",
              icon: "dot",
              query: {
                is_published__eq: null,
                is_deleted__eq: null,
              },
            },
            {
              key: "common:navigation.not_published_line_sheets",
              pathname: "/line-sheets",
              icon: "dot",
              query: {
                is_published__eq: false,
                is_deleted__eq: false,
              },
            },
            {
              key: "common:navigation.published_line_sheets",
              pathname: "/line-sheets",
              icon: "dot",
              query: {
                is_published__eq: true,
                is_deleted__eq: false,
              },
            },
            {
              key: "common:navigation.deleted_line_sheets",
              pathname: "/line-sheets",
              icon: "dot",
              query: {
                is_deleted__eq: true,
              },
            },
          ],
        },
        {
          key: "navigation.ordered_line_sheets",
          pathname: "/order-sheets",
          icon: "clipboard-check",
          children: [
            {
              key: "navigation.all_ordered_line_sheets",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                status__in: [
                  "ORDERED_TO_AGENCY",
                  "AGENCY_CONFIRMED",
                  "ORDERED_TO_BOUTIQUE",
                  "RESUBMIT_REQUIRED",
                  "RESUBMIT_COMPLETED",
                  "CANCELED",
                  // "MODIFICATION_REQUIRED",
                  // "MODIFICATION_COMPLETED",
                  "BOUTIQUE_CONFIRMED",
                  "BUYER_CONFIRMED",
                  "TRANSPORTATION_ING",
                  "TRANSPORTATION_COMPLETED",
                  "COMPLETED",
                ],
              },
            },
            {
              key: "common:navigation.order_submitted",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["ORDERED_TO_AGENCY"],
              },
            },
            // {
            //   key: "common:navigation.agency_confirmed_line_sheets",
            //   pathname: "/order-sheets",
            //   icon: "dot",
            //   query: {
            //     order_sheet_status__in: ["AGENCY_CONFIRMED"],
            //   },
            // },
            {
              key: "common:navigation.order_consulting",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["ORDERED_TO_BOUTIQUE"],
              },
            },
            {
              key: "common:navigation.buyer_modification_required",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["RESUBMIT_REQUIRED"],
              },
            },
            {
              key: "common:navigation.resubmit_completed",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["RESUBMIT_COMPLETED"],
              },
            },
            {
              key: "common:navigation.buyer_confirm_required",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["BOUTIQUE_CONFIRMED"],
              },
            },
            {
              key: "common:navigation.order_confirmed",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["BUYER_CONFIRMED"],
              },
            },
            {
              key: "common:navigation.transportation_ing",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["TRANSPORTATION_ING"],
              },
            },
            {
              key: "common:navigation.transportation_completed",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["TRANSPORTATION_COMPLETED"],
              },
            },
            {
              key: "common:navigation.completed_line_sheets",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["COMPLETED"],
              },
            },
            {
              key: "common:navigation.canceled_line_sheets",
              pathname: "/order-sheets",
              icon: "dot",
              query: {
                order_sheet_status__in: ["CANCELED"],
              },
            },
          ],
        },
        // 문의/답변 — 오더 상태와 상관없이(제출 전 오더 포함) 문의가 있는 오더를 모아 본다
        {
          key: "common:navigation.inquiries",
          pathname: "/inquiries",
          icon: "cs",
          rightElement: <InquiryNavCountBadge />,
        },
        {
          key: "credits",
          pathname: "/credit-invoices",
          icon: "coin",
          predicates: [
            AppUtils.isContainsLiteral,
            AppUtils.isLiteralEditOrUpload,
          ],
        },
        {
          key: "deposit_invoices",
          pathname: "/deposit-invoices",
          icon: "bank",
          predicates: [
            AppUtils.isContainsLiteral,
            AppUtils.isLiteralEditOrUpload,
          ],
        },
        // {
        //   ...invoices,
        //   children: [
        //     ...invoices.children,
        //     {
        //       key: "triangular_trades",
        //       pathname: "/triangular-trades",
        //       icon: "dot",
        //       predicates: [
        //         AppUtils.isContainsLiteral,
        //         AppUtils.isLiteralEditOrUpload,
        //       ],
        //     },
        //   ],
        // },
        // transportations,
      ];
    }
  } else if (identity?.company?.type === "BOUTIQUE") {
    return [
      {
        key: "dashboard",
        pathname: "/dashboard",
        icon: "chart-bar",
      },
      {
        key: "navigation.line_sheets",
        pathname: "/line-sheets",
        icon: "copy",
      },
      {
        key: "common:navigation.ordered_line_sheets",
        pathname: "/order-sheets",
        icon: "clipboard-check",
        query: {
          status__in: [
            "ORDERED_TO_BOUTIQUE",
            "RESUBMIT_COMPLETED",
            "CANCELED",
            // "MODIFICATION_REQUIRED",
            // "MODIFICATION_COMPLETED",
            "BOUTIQUE_CONFIRMED",
            // 바이어가 확정한 뒤 운송까지도 부티크가 제 오더를 따라가 볼 수 있게 한다
            "BUYER_CONFIRMED",
            "TRANSPORTATION_ING",
            "TRANSPORTATION_COMPLETED",
            "COMPLETED",
          ],
        },
      },
      {
        key: "common:navigation.invoices",
        pathname: "/invoices",
        icon: "document-text",
        children: [
          {
            key: "order_confirms",
            pathname: "/order-confirmation-invoices",
            icon: "dot",
            predicates: [
              AppUtils.isContainsLiteral,
              AppUtils.isLiteralEditOrUpload,
            ],
          },
          {
            key: "proformas",
            pathname: "/proforma-invoices",
            icon: "dot",
            predicates: [
              AppUtils.isContainsLiteral,
              AppUtils.isLiteralEditOrUpload,
            ],
          },
          {
            key: "triangular_trades",
            pathname: "/triangular-trades",
            icon: "dot",
            predicates: [
              AppUtils.isContainsLiteral,
              AppUtils.isLiteralEditOrUpload,
            ],
          },
        ],
      },
    ];
  }

  return [];
}

type MobileMenuRule = {
  key: string;
  excludeChildKeys?: string[];
  // 지정하면 이 키의 하위 메뉴만 남긴다. 남는 하위가 없으면 최상위 메뉴도 노출하지 않는다.
  includeChildKeys?: string[];
};

// 모바일에서 노출할 메뉴만 화이트리스트로 선언한다.
// 여기에 없는 회사 타입(BOUTIQUE 등)은 데스크탑과 동일한 전체 메뉴를 유지한다.
const MOBILE_MENU_RULES: Record<string, MobileMenuRule[]> = {
  // 오더 관리는 모바일에서 노출하지 않는다 — 바이어 모바일 메뉴는 패스픽만 남긴다.
  BUYER: [
    {
      // 패스픽은 상품 찾기·담은 상품·등록 신청 내역·주문 내역·멤버십을 노출한다.
      // 미가입(NONE)이면 리터럴 하위가 멤버십뿐이라 멤버십 한 줄만 남는다 —
      // 그래도 최상위 메뉴는 살아서, 미가입자도 여기서 멤버십으로 들어갈 수 있다.
      // 목록은 모바일 진입 화면과 같은 소스를 쓴다.
      key: PATHPICK_MOBILE_SECTION_KEY,
      includeChildKeys: PATHPICK_MOBILE_CHILD_KEYS,
    },
  ],
  // 에이전시는 아직 모바일 반응형 대상이 아니다. 데스크탑과 같은 순서로 노출만 시킨다.
  AGENCY: [
    { key: "navigation.line_sheets" },
    { key: "navigation.ordered_line_sheets" },
  ],
};

function filterMobileLiterals(
  literals: LiteralItem[],
  companyType: string | undefined
): LiteralItem[] {
  const rules = companyType ? MOBILE_MENU_RULES[companyType] : undefined;
  if (!rules) return literals;

  return rules.flatMap(({ key, excludeChildKeys, includeChildKeys }) => {
    const literal = literals.find((i) => i.key === key);
    if (!literal) return [];

    if (includeChildKeys?.length) {
      const children = literal.children?.filter((child) =>
        includeChildKeys.includes(child.key)
      );
      if (!children?.length) return [];
      return [{ ...literal, children }];
    }

    if (!excludeChildKeys?.length) return [literal];
    return [
      {
        ...literal,
        children: literal.children?.filter(
          (child) => !excludeChildKeys.includes(child.key)
        ),
      },
    ];
  });
}

function calc(
  literal: LiteralItem,
  location: Location,
  prev?: Item,
  defaultCollapsed = true
): Item {
  const item: Item = {
    ...literal,
    children: [],
    isActive: false,
    isCollapsed: prev ? prev.isCollapsed : defaultCollapsed,
  };
  //
  // if (literal.pathname === "/transportations") {
  //   debugger;
  // }

  const predicates =
    literal.predicates && literal.predicates.length > 0
      ? [...literal.predicates]
      : [AppUtils.isContainsLiteral];
  const referer = AppUtils.getReferer(location);

  if (referer) {
    [...predicates].forEach((predicate) => {
      predicates.push((literal, location) => {
        return predicate(literal, referer);
      });
    });
  }

  item.isActive = predicates.some((predicate) => predicate(literal, location));

  item.children = (literal.children || []).map((literalChild) => {
    return calc(
      literalChild,
      location,
      AppUtils.findDirectChild(
        prev?.children || [],
        (i) => i.key === literalChild.key
      )
    );
  });

  const isLeaf = item.children.length === 0;
  if (!isLeaf && item.isCollapsed && item.children.some((i) => i.isActive)) {
    item.isCollapsed = false;
  }

  return item;
}

// 계정 단위 ORDER 제한 시 숨기는 최상위 메뉴
const ORDER_MENU_KEYS = [
  "navigation.buyer.order",
  "navigation.buyer.order_management",
];

interface AppNavigationProps {
  isMobile?: boolean;
}

export default function AppNavigation({
  isMobile = false,
}: AppNavigationProps) {
  const identity = useIdentity();
  const { data: mySelf } = useGetMySelf();
  const { membershipStatus, isMembershipLoading } = useMembership();
  const { isOrderRestricted } = useRestrictedFeatures();
  const [items, setItems] = useState<Item[]>([]);
  const location = useLocation();

  const features = useMemo(() => {
    if (!mySelf || !identity?.company) return [];
    const currentCompany = mySelf.companies.find(
      (c) => c.id === identity.company!.id
    );
    return currentCompany?.features ?? [];
  }, [mySelf, identity?.company]);

  useEffect(() => {
    // 데이터 확정 전에는 메뉴를 만들지 않는다 (상태 기본값 NONE으로 인한 플래시 방지)
    if (isMembershipLoading) return;
    setItems((prev) => {
      const literals = getLiterals(identity, features, membershipStatus).filter(
        // 오더가 막힌 계정은 오더하기/오더 관리 메뉴를 통째로 뺀다(라우트는 OrderAccessGuard가 막는다).
        (literal) => !(isOrderRestricted && ORDER_MENU_KEYS.includes(literal.key))
      );
      const visibleLiterals = isMobile
        ? filterMobileLiterals(literals, identity?.company?.type)
        : literals;

      return visibleLiterals.map((literal) => {
        return calc(
          literal,
          location,
          AppUtils.findDirectChild(prev, (i) => i.key === literal.key),
          !isMobile // 모바일은 최상위 메뉴를 펼친 상태로 시작
        );
      });
    });
  }, [
    identity,
    location,
    features,
    membershipStatus,
    isMembershipLoading,
    isOrderRestricted,
    isMobile,
  ]);

  const handleClick = useCallback((item: Item) => {
    if (item.children.length > 0) {
      setItems((prev) => {
        return AppUtils.updateNestedObjects(prev, [
          {
            predicate: (i) => i.key === item.key,
            updater: (i) => {
              return {
                ...i,
                isCollapsed: !i.isCollapsed,
              };
            },
          },
        ]);
      });
    }
  }, []);

  // 멤버십 데이터 확정 전에는 스켈레톤을 노출해 잘못된 메뉴가 잠깐 보이는 것을 막는다
  if (isMembershipLoading) {
    return <AppNavigationSkeleton />;
  }

  return (
    <Flex
      direction={"column"}
      gap={"12px"}
      justifyContent={"stretch"}
      paddingTop={"12px"}
    >
      {items.map((item, index) => (
        <AppNavigationLink
          item={item}
          key={`AppNavigationLink_${index}`}
          onClick={handleClick}
        />
      ))}
    </Flex>
  );
}

function AppNavigationSkeleton() {
  return (
    <Flex
      direction={"column"}
      gap={"12px"}
      paddingTop={"12px"}
      paddingX={"12px"}
    >
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton
          key={`AppNavigationSkeleton_${index}`}
          height={"32px"}
          borderRadius={"8px"}
        />
      ))}
    </Flex>
  );
}
